import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loading, Tabs } from '../../../uiComponents/common';
import FormRow from '../../../components/forms/formRow';
import EventProductItem from './eventProductItem';
import AddProductsModal from '../modals/addProductsModal';

const EventProductList = forwardRef(({ specialDateId, restaurantId, queryData, isLoadingData, isNew, onPendingChange, initialTab }, ref) => {
  const { t } = useTranslation('translation', { keyPrefix: 'screens.fechasEspeciales' });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab || 'area');
  const productRefs = useRef({});

  const groupedProducts = queryData?.products || [];

  const eventProducts = useMemo(
    () =>
      groupedProducts.map((product) => ({
        id: product.productId,
        productId: product.productId,
        specialDateId: queryData?.specialDateId || specialDateId,
        product: { name: product.productName },
        reservationType: product.reservationType,
        dateRangePrices: (product.prices || []).map((p) => ({ ...p, weekDay: p.weekDay || p.weekday })),
      })),
    [groupedProducts, specialDateId],
  );

  const linkedProductIds = useMemo(() => eventProducts.map((ep) => ep.productId), [eventProducts]);

  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const refs = Object.values(productRefs.current).filter(Boolean);
      if (refs.length === 0) return true;
      const results = await Promise.all(refs.map((r) => r.validate()));
      const firstInvalid = refs.find((_, i) => !results[i]);
      if (firstInvalid) firstInvalid.expand();
      return results.every(Boolean);
    },
    getAllPrices: () => {
      const prices = [];
      Object.values(productRefs.current)
        .filter(Boolean)
        .forEach((r) => {
          const { productId, dateRangePrices } = r.getData();
          dateRangePrices.forEach((p) => {
            prices.push({
              productId,
              weekday: p.weekDay,
              price: p.price,
              priceExtraPax: p.priceExtraPax,
              consumption: p.consumption,
              consumptionPerExtraPax: p.consumptionPerExtraPax,
              depositPercentage: p.depositPercentage,
              taxPercentage: p.taxPercentage,
              tipPercentage: p.tipPercentage,
              stock: p.stock,
              isActive: p.isActive,
            });
          });
        });
      return prices;
    },
  }));

  const handleAddProducts = (newProducts, tab) => {
    if (tab) setActiveTab(tab);
    const newEntries = newProducts.map((product) => ({
      productId: product.productId,
      productName: product.productName,
      reservationType: product.reservationType || (tab === 'group' ? 'GROUP' : 'AREA'),
      prices: (product.prices || []).map((p) => ({
        ...p,
        weekDay: p.weekDay || p.weekday,
        weekday: p.weekDay || p.weekday,
      })),
    }));
    onPendingChange((prev) => [...prev, ...newEntries]);
    setAddModalOpen(false);
  };

  const handleUnlink = (productId) => {
    delete productRefs.current[productId];
    onPendingChange((prev) => prev.filter((p) => p.productId !== productId));
  };

  const tabs = [
    { type: 'area', title: t('detail.sectionProducts.tabArea'), isActive: activeTab === 'area' },
    { type: 'group', title: t('detail.sectionProducts.tabGroup'), isActive: activeTab === 'group' },
  ];

  const getProductTab = (item) => {
    const type = (item.reservationType || '').toUpperCase();
    return type === 'GROUP' ? 'group' : 'area';
  };

  const getProductMode = (item) => {
    const type = (item.reservationType || '').toUpperCase();
    if (type === 'GROUP') return 'groups';
    return 'areas';
  };

  const filteredProducts = eventProducts.filter((item) => getProductTab(item) === activeTab);

  const hasRestaurant = !!restaurantId;

  if (isLoadingData) {
    return (
      <FormRow
        text={t('detail.sectionProducts.title')}
        description={t('detail.sectionProducts.description')}
        endComponent={
          <div className='col-12 mt-2 mb-2'>
            <Loading />
          </div>
        }
      />
    );
  }

  if (isNew && !hasRestaurant) {
    return (
      <FormRow
        text={t('detail.sectionProducts.title')}
        description={t('detail.sectionProducts.description')}
        endComponent={
          <div className='col-12 mt-2 mb-2'>
            <div className='fe-products-empty'>
              <div className='fe-products-empty-hint'>
                {t('detail.sectionProducts.selectRestaurantFirst')}
              </div>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <>
      <FormRow
        text={t('detail.sectionProducts.title')}
        description={t('detail.sectionProducts.description')}
        withBorder={false}
        endComponent={
          <div className='col-12 mt-2 mb-2'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <Tabs options={tabs} onClick={(type) => setActiveTab(type)} />
              <Button
                size='medium'
                className='btn-wellet-secondary'
                text={t('detail.sectionProducts.addProduct')}
                onClick={() => setAddModalOpen(true)}
              />
            </div>

            {filteredProducts.length === 0 && (
              <div className='fe-products-empty'>
                <div className='fe-products-empty-title'>
                  {t('detail.sectionProducts.noProducts')}
                </div>
                <div className='fe-products-empty-hint'>
                  {t('detail.sectionProducts.noProductsHint')}
                </div>
              </div>
            )}

            {eventProducts.map((item) => (
              <div key={item.id} style={{ display: getProductTab(item) === activeTab ? undefined : 'none' }}>
                <EventProductItem
                  ref={(el) => { productRefs.current[item.productId] = el; }}
                  item={item}
                  mode={getProductMode(item)}
                  onDelete={handleUnlink}
                  defaultExpanded={false}
                />
              </div>
            ))}
          </div>
        }
      />

      <AddProductsModal
        open={addModalOpen}
        setOpen={setAddModalOpen}
        linkedProductIds={linkedProductIds}
        onConfirm={handleAddProducts}
        isLoading={false}
        restaurantId={restaurantId}
      />
    </>
  );
});

EventProductList.displayName = 'EventProductList';

export default EventProductList;
